/* THE CONVERSATION TAB                                                (PRD 6)
 *
 * The thread with this member, in the order it happened, with who sent each message.
 *
 * PHOTON OWNS THE MESSAGES AND ACROSTIC STORES NONE OF THEM. That is the same rule
 * the record follows everywhere else: one store for the words, because a duplicated
 * message is a message that can come to disagree with itself. In the demo there is
 * no Photon, so the thread is read out of the seeded event stream - but the shape of
 * the screen is the shape it has against the real thing: read the thread, render it,
 * send into it, keep nothing.
 *
 * IT NEVER RENDERS AN EMPTY THREAD WHEN IT SIMPLY COULD NOT READ ONE. "An empty
 * thread reads as a member nobody has spoken to, which is a lie about the member."
 * So there are three states and they say different things: a thread, a member nobody
 * has messaged yet, and a channel that could not be reached.
 *
 * THE TIMELINE AND THIS TAB DO NOT RESTATE EACH OTHER. The timeline records THAT a
 * message happened, with its direction, its sender and its time. This is where the
 * words are.
 */
import { useAsync, useSource } from '../../data/SourceProvider.js';
import { NoneRow, Panel } from '../../components/Panel.js';
import { fmt } from '../../lib/fmt.js';
import type { Member } from '@acrostic/model';

export function Conversation({ m }: { m: Member }) {
  const source = useSource();
  const { data, error } = useAsync(() => source.getConversation(m.id), [source, m.id]);

  if (error) return <NoneRow>Could not read the thread. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading the thread…</NoneRow>;

  /* THE CHANNEL COULD NOT BE REACHED. Not the same as a quiet member, and the
     difference is the whole reason this branch exists. */
  if (data.channel === 'unreachable') {
    return (
      <Panel eyebrow="The messaging channel" title="Conversation">
        <NoneRow>
          The message channel could not be reached, so this thread is not being shown.
          It is not that nobody has spoken to {m.firstName}.
        </NoneRow>
      </Panel>
    );
  }

  /* NO NUMBER ON THE RECORD IS A DATA-QUALITY FINDING, not an empty thread (PRD 6).
     A thread is matched on the phone number already held, so an unmatched one is
     usually a member whose number is wrong rather than a member nobody has texted. */
  if (data.channel === 'unmatched') {
    return (
      <Panel eyebrow="The messaging channel" title="Conversation">
        <NoneRow>
          No phone number on {m.firstName}&rsquo;s record, so there is nothing to match a
          thread to. Add one on Details and the history appears here.
        </NoneRow>
      </Panel>
    );
  }

  return (
    <Panel
      eyebrow={data.sending
        ? 'Sent and received on the gym’s connected channel'
        : 'Reading only. Sending is off until Acrostic turns it on for this gym'}
      title="Conversation"
    >
      {!data.messages.length ? (
        <NoneRow>
          Nobody has messaged {m.firstName} yet. That is a fact about the gym rather
          than about them.
        </NoneRow>
      ) : (
        <div className="thread">
          {data.messages.map((msg, i) => (
            <div key={`${msg.at}-${i}`} className={'msg ' + (msg.inbound ? 'in' : 'out')}>
              <div className="bubble t-body-small">{msg.text}</div>
              <div className="meta t-caption muted">
                {msg.inbound ? m.firstName : msg.who} · {fmt.date(msg.at)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* THE COMPOSER IS PRESENT AND HONEST. It is not hidden when sending is off:
          a missing box says the product cannot message, and what is true is that
          this gym's number is not provisioned yet. Nothing here promises a channel
          that is not live. */}
      <div className="composer">
        <textarea
          className="field" rows={2} disabled={!data.sending}
          placeholder={data.sending
            ? `Message ${m.firstName}`
            : 'Sending is off for this gym. The thread above is real history.'}
        />
        <button type="button" className="btn primary t-label-medium" disabled={!data.sending}>
          Send
        </button>
      </div>
    </Panel>
  );
}
